import { useCallback, useEffect, useState, useMemo } from "react";
import { api, idr } from "../lib/api";
import { Target, Users, Zap, RefreshCw, Send, Award, Settings, X, Filter, TrendingUp, Download, Search, Layers, Percent } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import InputRupiah from "../components/InputRupiah";
import { MonthPicker } from "@/components/ui/date-picker";
import LoadingState from "@/components/ui/loading-state";
import { bulanIniISO } from "@/lib/tanggal";
import { useModalGuard, UnsavedGuardBar } from "@/hooks/useModalGuard";
import { useSortState, sortRows, SortTh } from "@/components/ui/sort";

// Replika `hitung_insentif` backend (routers/poin.py) — HANYA dipakai untuk
// pratinjau tampilan kartu saat /sales/targets belum menyertakan rincian
// insentif berjenjang per baris. Sumber kebenaran resmi tetap slip gaji.
const DEFAULT_TIERS_TAMPILAN = [
  { min_pct: 70, max_pct: 80, cair_pct: 50 },
  { min_pct: 80, max_pct: 100, cair_pct: 80 },
  { min_pct: 100, max_pct: 120, cair_pct: 100 },
  { min_pct: 120, max_pct: null, cair_pct: 120 },
];
function hitungInsentifTampilan(realisasiPoin, targetPoin, bobot, tiers) {
  const target = Number(targetPoin) || 0;
  const b = Number(bobot) || 0;
  const realisasi = Number(realisasiPoin) || 0;
  const insentifDasar = Math.round(target * b * 100) / 100;
  const pctRealisasi = target ? Math.round((realisasi / target) * 100 * 100) / 100 : 0;
  const ordered = [...(tiers || [])].sort((a, b2) => (Number(a.min_pct) || 0) - (Number(b2.min_pct) || 0));
  let tierCairPct = 0;
  for (const t of ordered) {
    const mn = Number(t.min_pct) || 0;
    const mx = t.max_pct === null || t.max_pct === undefined || t.max_pct === "" ? null : Number(t.max_pct);
    if (pctRealisasi >= mn && (mx === null || pctRealisasi < mx)) { tierCairPct = Number(t.cair_pct) || 0; break; }
  }
  const insentifCair = Math.round(((insentifDasar * tierCairPct) / 100) * 100) / 100;
  return { insentifDasar, pctRealisasi, tierCairPct, insentifCair };
}

export default function SalesTargets() {
  const { user } = useAuth();
  const canEdit = user?.role === "superadmin" || user?.role === "admin";
  const [periode, setPeriode] = useState(bulanIniISO());
  const [gradeFilter, setGradeFilter] = useState("");
  const [divisiFilter, setDivisiFilter] = useState("");
  const [data, setData] = useState(null);
  const [sending, setSending] = useState(false);
  const [setupOpen, setSetupOpen] = useState(false);
  const [detailSel, setDetailSel] = useState(null); // baris karyawan diklik → modal detail (data yang sudah dimuat, tanpa panggilan baru)
  const [emps, setEmps] = useState([]); // untuk peta divisi per karyawan (brief #27)
  const [poinCfg, setPoinCfg] = useState(null); // untuk tier rumus insentif (brief #26)

  const load = useCallback(() => {
    const p = new URLSearchParams();
    p.set("periode", periode);
    if (gradeFilter) p.set("grade_jabatan", gradeFilter);
    api.get(`/sales/targets?${p}`).then(r => setData(r.data));
  }, [periode, gradeFilter]);
  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);
  useEffect(() => {
    api.get("/employees").then(r => setEmps(r.data || [])).catch(() => {});
    api.get("/poin/config").then(r => setPoinCfg(r.data)).catch(() => {});
  }, []);

  // Peta employee_id -> divisi, sumber nilai unik utk dropdown saring (pola KpiTargets.jsx)
  const empDivisiMap = useMemo(() => Object.fromEntries(emps.map(e => [e.id, e.divisi])), [emps]);
  const divisiOptions = useMemo(() => [...new Set(emps.map(e => e.divisi).filter(Boolean))].sort(), [emps]);
  const insentifTiers = useMemo(
    () => (poinCfg?.rumus_insentif?.template?.length ? poinCfg.rumus_insentif.template : DEFAULT_TIERS_TAMPILAN),
    [poinCfg]
  );

  // Brief #27 (saring divisi) + #28 (urut menurun berdasarkan % pencapaian —
  // backend sudah mengurutkan begini, disini dijaga eksplisit setelah difilter).
  const visibleTeam = useMemo(() => {
    if (!data) return [];
    let t = data.team;
    if (divisiFilter) t = t.filter(s => empDivisiMap[s.employee_id] === divisiFilter);
    return [...t].sort((a, b) => b.realisasi_pct - a.realisasi_pct);
  }, [data, divisiFilter, empDivisiMap]);

  const notifOwner = async () => {
    setSending(true);
    try {
      const { data: r } = await api.post("/sales/notify-wa-owner");
      const tg = r.channels?.telegram;
      const wa = r.channels?.whatsapp;
      if (tg?.ok || wa?.ok) {
        const sent = [tg?.ok && "Telegram", wa?.ok && "WhatsApp"].filter(Boolean).join(" + ");
        toast.success(`Terkirim via ${sent}`);
      } else if (!r.twilio_configured && !r.telegram_configured) {
        toast.info("Belum ada channel aktif — preview di console. Isi TELEGRAM_BOT_TOKEN atau TWILIO_* di .env.");
      } else {
        toast.error("Semua channel gagal — cek console.");
      }
      if (r.message_preview) console.log(r.message_preview);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
    setSending(false);
  };

  const exportExcel = async () => {
    const token = localStorage.getItem("hris_token");
    const p = new URLSearchParams();
    if (periode) p.set("periode", periode);
    if (gradeFilter) p.set("grade_jabatan", gradeFilter);
    const base = process.env.REACT_APP_BACKEND_URL;
    const res = await fetch(`${base}/api/export/sales-targets?${p}`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) { toast.error("Gagal export"); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `TargetRealisasi_${periode}.xlsx`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    toast.success("Export Excel diunduh");
  };

  // Urutan bawaan dipertahankan sampai header diklik. Dideklarasikan sebelum
  // early-return "loading" di bawah supaya urutan hook React tetap konsisten.
  const { sortKey: gradeSortKey, sortDir: gradeSortDir, toggle: toggleGradeSort } = useSortState();

  if (!data) return <div className="p-4 md:p-8 max-w-[1400px]"><LoadingState loading data-testid="sales-targets-loading" /></div>;

  return (
    <div className="p-4 md:p-8 max-w-[1400px]">
      <header className="flex items-start justify-between mb-6 md:mb-8 flex-wrap gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-emerald-600 mb-2">Live Dashboard</p>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900">Target vs Realisasi Sales</h1>
          <p className="mt-2 text-sm text-slate-500 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            Live • Refresh setiap 60 detik • Periode {periode}
            {gradeFilter && <span className="ml-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-medium">🎯 {gradeFilter}</span>}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <MonthPicker value={periode} onChange={setPeriode}
            data-testid="targets-periode" />
          <div className="inline-flex items-center gap-1.5 h-10 px-3 rounded-lg border border-slate-200 bg-white">
            <Filter className="w-3.5 h-3.5 text-slate-500" />
            <select value={gradeFilter} onChange={e => setGradeFilter(e.target.value)}
              data-testid="targets-grade-filter"
              className="text-sm bg-transparent outline-none min-w-[140px]">
              <option value="">Semua Jabatan</option>
              {(data.available_grades || []).map(g => <option key={g} value={g}>{g}</option>)}
            </select>
          </div>
          {divisiOptions.length > 0 && (
            <div className="inline-flex items-center gap-1.5 h-10 px-3 rounded-lg border border-slate-200 bg-white">
              <Filter className="w-3.5 h-3.5 text-slate-500" />
              <select value={divisiFilter} onChange={e => setDivisiFilter(e.target.value)}
                data-testid="st-filter-divisi"
                className="text-sm bg-transparent outline-none min-w-[140px]">
                <option value="">Semua Divisi</option>
                {divisiOptions.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
          )}
          <button onClick={load} data-testid="targets-refresh"
            className="h-10 px-3 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm inline-flex items-center gap-1.5">
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
          <button onClick={exportExcel} data-testid="btn-export-targets"
            className="h-10 px-4 rounded-lg border border-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 text-sm font-medium inline-flex items-center gap-2">
            <Download className="w-4 h-4" /> Export Excel
          </button>
          {canEdit && (
            <>
              <button onClick={() => setSetupOpen(true)} data-testid="btn-setup-target"
                className="h-10 px-4 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium inline-flex items-center gap-2">
                <Settings className="w-4 h-4" /> Setup Target
              </button>
              <button onClick={notifOwner} disabled={sending} data-testid="btn-notif-owner"
                className="h-10 px-4 rounded-lg bg-sky-600 hover:bg-sky-700 text-white text-sm font-medium inline-flex items-center gap-2 disabled:opacity-60">
                <Send className="w-4 h-4" /> {sending ? "Mengirim..." : "Notif Owner (Telegram/WA)"}
              </button>
            </>
          )}
        </div>
      </header>

      {/* Summary KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-5 mb-6 md:mb-8">
        <Kpi icon={Users} label="Tim Sales" value={fmtNum(data.summary.team_size)} tint="slate" />
        <Kpi icon={Target} label="Total Target" value={`${fmtNum(data.summary.total_target)} poin`} tint="sky" />
        <Kpi icon={Zap} label="Total Realisasi" value={`${fmtNum(data.summary.total_realisasi)} poin`} tint="emerald"
          sub={`${data.summary.overall_pct}% dari target`} />
        <Kpi icon={Award} label="On-Track (≥100%)" value={`${data.summary.on_track} orang`} tint="amber" />
      </div>

      {/* Overall progress bar */}
      <div className="bg-gradient-to-r from-[#0F172A] to-emerald-900 rounded-xl p-5 md:p-8 text-white mb-6 shadow-lg">
        <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
          <div>
            <p className="text-xs uppercase tracking-wider text-white/60">Overall Team Progress</p>
            <p className="font-display text-3xl md:text-4xl font-bold mt-1">{data.summary.overall_pct}%</p>
          </div>
          <div className="text-right">
            <p className="text-xs text-white/60">Realisasi</p>
            <p className="font-mono text-lg md:text-2xl font-bold">{data.summary.total_realisasi} <span className="text-sm md:text-lg text-white/60">/ {data.summary.total_target} poin</span></p>
          </div>
        </div>
        <div className="h-4 rounded-full bg-white/10 overflow-hidden">
          <motion.div initial={{ width: 0 }} animate={{ width: `${Math.min(100, data.summary.overall_pct)}%` }}
            transition={{ duration: 1 }}
            className={`h-full ${data.summary.overall_pct >= 100 ? "bg-emerald-400" : data.summary.overall_pct >= 75 ? "bg-emerald-500" : data.summary.overall_pct >= 50 ? "bg-amber-500" : "bg-rose-500"}`} />
        </div>
      </div>

      {/* Rekap per Jabatan */}
      {data.by_grade?.length > 0 && (
        <section className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-base md:text-lg font-bold text-slate-900 inline-flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-emerald-600" /> Rekap Pencapaian per Jabatan
            </h2>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-x-auto">
            <table className="w-full text-sm min-w-[700px]" data-testid="rekap-jabatan-table">
              <thead>
                <tr className="bg-slate-50 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  <SortTh label="Jabatan" sortKeyName="grade_jabatan" sortKey={gradeSortKey} sortDir={gradeSortDir} onSort={toggleGradeSort} testid="rekap-sort-jabatan" />
                  <SortTh label="Karyawan" sortKeyName="n_karyawan" sortKey={gradeSortKey} sortDir={gradeSortDir} onSort={toggleGradeSort} align="right" testid="rekap-sort-karyawan" />
                  <SortTh label="Target" sortKeyName="total_target" sortKey={gradeSortKey} sortDir={gradeSortDir} onSort={toggleGradeSort} align="right" testid="rekap-sort-target" />
                  <SortTh label="Realisasi" sortKeyName="total_realisasi" sortKey={gradeSortKey} sortDir={gradeSortDir} onSort={toggleGradeSort} align="right" testid="rekap-sort-realisasi" />
                  <SortTh label="Pencapaian" sortKeyName="pct" sortKey={gradeSortKey} sortDir={gradeSortDir} onSort={toggleGradeSort} align="right" testid="rekap-sort-pct" />
                  <SortTh label="On-Track" sortKeyName="on_track" sortKey={gradeSortKey} sortDir={gradeSortDir} onSort={toggleGradeSort} align="right" testid="rekap-sort-ontrack" />
                  <SortTh label="Omzet" sortKeyName="omzet" sortKey={gradeSortKey} sortDir={gradeSortDir} onSort={toggleGradeSort} align="right" testid="rekap-sort-omzet" />
                </tr>
              </thead>
              <tbody>
                {sortRows(data.by_grade, gradeSortKey, gradeSortDir).map(g => (
                  <tr key={g.grade_jabatan} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                    onClick={() => setGradeFilter(g.grade_jabatan === "-" ? "" : g.grade_jabatan)}
                    data-testid={`rekap-${g.grade_jabatan}`}>
                    <td className="py-3 px-4 font-medium text-slate-900">{g.grade_jabatan}</td>
                    <td className="py-3 px-4 text-right">{g.n_karyawan}</td>
                    <td className="py-3 px-4 text-right font-mono">{g.total_target}</td>
                    <td className="py-3 px-4 text-right font-mono">{g.total_realisasi}</td>
                    <td className="py-3 px-4 text-right">
                      <div className="flex items-center gap-2 justify-end">
                        <div className="w-16 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                          <div className={`h-full ${
                            g.pct >= 100 ? "bg-emerald-500" :
                            g.pct >= 75 ? "bg-sky-500" :
                            g.pct >= 50 ? "bg-amber-500" : "bg-rose-500"
                          }`} style={{ width: `${Math.min(100, g.pct)}%` }} />
                        </div>
                        <span className={`text-xs font-bold ${g.pct >= 100 ? "text-emerald-700" : "text-slate-700"}`}>{g.pct}%</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-right">{g.on_track} / {g.n_karyawan}</td>
                    <td className="py-3 px-4 text-right font-mono text-xs">{idr(g.omzet)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-slate-400 mt-2">Klik baris untuk memfilter tampilan sales card di bawah.</p>
        </section>
      )}

      {/* Per sales cards */}
      {visibleTeam.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-16 text-center">
          <Target className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="font-display font-semibold text-slate-900">
            {gradeFilter || divisiFilter
              ? `Belum ada sales dengan filter ini${gradeFilter ? ` · jabatan ${gradeFilter}` : ""}${divisiFilter ? ` · divisi ${divisiFilter}` : ""}`
              : "Belum ada tim sales dengan target poin"}
          </p>
          <p className="text-sm text-slate-500 mt-1">
            {canEdit ? (
              <>Klik <button onClick={() => setSetupOpen(true)} className="text-emerald-700 font-medium underline">Setup Target</button> untuk memberi target poin per karyawan.</>
            ) : "Hubungi HR untuk setup target."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {visibleTeam.map((s, i) => {
            // Brief #26: kartu insentif berjenjang. `/sales/targets` belum
            // menyertakan rincian per baris — pakai kalau backend sudah kirim
            // (future-proof), kalau belum, hitung TAMPILAN dari poin/target
            // dengan tier config /poin/config. Angka RESMI tetap dari slip gaji.
            const insentifCalc = s.insentif_rincian
              ? {
                  pctRealisasi: s.insentif_rincian.pct_realisasi,
                  tierCairPct: s.insentif_rincian.tier_cair_pct,
                  insentifDasar: s.insentif_rincian.insentif_dasar,
                  insentifCair: s.insentif_rincian.insentif_cair,
                }
              : hitungInsentifTampilan(s.realisasi_poin, s.target_poin, s.bobot_per_poin, insentifTiers);
            return (
            <motion.div key={s.employee_id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
              data-testid={`sales-card-${s.employee_id}`}
              className="bg-white border border-slate-200 rounded-xl p-5 md:p-6 shadow-sm">
              <div className="flex items-start justify-between mb-4">
                <div className="min-w-0">
                  <button type="button"
                    onClick={() => setDetailSel({ s, divisi: empDivisiMap[s.employee_id], insentifCalc, tiers: insentifTiers })}
                    data-testid={`st-detail-${s.employee_id}`}
                    className="font-display font-semibold text-slate-900 truncate hover:text-emerald-700 hover:underline text-left">
                    {s.nama}
                  </button>
                  <p className="text-xs text-slate-500 truncate">{s.branch} · {s.grade_jabatan}</p>
                </div>
                <span className={`px-2 py-0.5 rounded-full text-xs font-bold shrink-0 ${
                  s.realisasi_pct >= 100 ? "bg-emerald-100 text-emerald-800" :
                  s.realisasi_pct >= 75 ? "bg-sky-100 text-sky-800" :
                  s.realisasi_pct >= 50 ? "bg-amber-100 text-amber-800" : "bg-rose-100 text-rose-800"
                }`}>{s.realisasi_pct}%</span>
              </div>
              <div className="mb-3">
                <div className="flex justify-between text-xs text-slate-500 mb-1">
                  <span>{s.realisasi_poin} poin</span>
                  <span>Target {s.target_poin}</span>
                </div>
                <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                  <div className={`h-full transition-all ${
                    s.realisasi_pct >= 100 ? "bg-emerald-500" :
                    s.realisasi_pct >= 75 ? "bg-sky-500" :
                    s.realisasi_pct >= 50 ? "bg-amber-500" : "bg-rose-500"
                  }`} style={{ width: `${Math.min(100, s.realisasi_pct)}%` }} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                <BreakdownBox label="HP sold" n={s.breakdown.sale_hp.n} pt={s.breakdown.sale_hp.poin} />
                <BreakdownBox label="ACC sold" n={s.breakdown.sale_acc.n} pt={s.breakdown.sale_acc.poin} />
                <BreakdownBox label="Akuisisi HP" n={s.breakdown.acq_hp.n} pt={s.breakdown.acq_hp.poin} />
                <BreakdownBox label="Akuisisi Dev" n={s.breakdown.acq_device.n} pt={s.breakdown.acq_device.poin} />
              </div>
              {s.target_upselling_pct > 0 && (
                <div className="mb-3 rounded-lg border border-sky-200 bg-sky-50/60 px-3 py-2"
                  data-testid={`upselling-block-${s.employee_id}`}>
                  <div className="flex items-center justify-between text-xs mb-1.5">
                    <span className="inline-flex items-center gap-1 font-medium text-sky-800">
                      <Layers className="w-3 h-3" /> Upselling
                    </span>
                    <span className="font-mono text-sky-900">
                      <span className="font-bold">{s.realisasi_upselling_pct}%</span>
                      <span className="text-sky-500"> / {s.target_upselling_pct}%</span>
                    </span>
                  </div>
                  <div className="h-1.5 rounded-full bg-white overflow-hidden">
                    <div className={`h-full transition-all ${
                      s.realisasi_upselling_pct >= s.target_upselling_pct ? "bg-emerald-500" :
                      s.upselling_achievement_pct >= 75 ? "bg-sky-500" :
                      s.upselling_achievement_pct >= 50 ? "bg-amber-500" : "bg-rose-500"
                    }`} style={{ width: `${Math.min(100, s.upselling_achievement_pct || 0)}%` }} />
                  </div>
                  <p className="text-[10px] text-sky-700 mt-1">
                    {s.upselling_multi_item} dari {s.upselling_total_transaksi} transaksi multi-item
                  </p>
                </div>
              )}
              {s.target_poin > 0 && (
                <div className="mb-3 rounded-lg border border-emerald-200 bg-emerald-50/60 px-3 py-2"
                  data-testid={`st-insentif-${s.employee_id}`}>
                  <div className="flex items-center justify-between text-xs mb-1.5">
                    <span className="inline-flex items-center gap-1 font-medium text-emerald-800">
                      <Percent className="w-3 h-3" /> Insentif Berjenjang
                    </span>
                    <span className="font-mono text-emerald-900">
                      <span className="font-bold">{insentifCalc.pctRealisasi}%</span>
                      <span className="text-emerald-600"> realisasi · cair {insentifCalc.tierCairPct}%</span>
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-[10px]">
                    <div className="bg-white rounded px-2 py-1">
                      <p className="text-emerald-600">Insentif Dasar</p>
                      <p className="font-mono font-bold text-emerald-900">{idr(insentifCalc.insentifDasar)}</p>
                    </div>
                    <div className="bg-white rounded px-2 py-1">
                      <p className="text-emerald-600">Insentif Cair</p>
                      <p className="font-mono font-bold text-emerald-900">{idr(insentifCalc.insentifCair)}</p>
                    </div>
                  </div>
                  <p className="text-[9px] text-emerald-600 italic mt-1.5">
                    {s.insentif_rincian ? "Angka resmi dari mesin payroll." : "Pratinjau tampilan — angka resmi mengikuti slip gaji."}
                  </p>
                </div>
              )}
              {/* SATU angka insentif per kartu (permintaan bos): footer ini dulu
                  menampilkan rumus datar lama (s.estimasi_insentif) yang berbeda
                  dari blok "Insentif Berjenjang" di atas — dua angka bertentangan
                  di satu kartu. Sekarang keduanya memakai insentifCalc.insentifCair
                  yang sama. */}
              <div className="pt-3 border-t border-slate-100 flex items-end justify-between">
                <div>
                  <p className="text-xs text-slate-500">Insentif Cair</p>
                  <p className="font-display font-bold text-emerald-700">{idr(insentifCalc.insentifCair)}</p>
                </div>
                <p className="text-[11px] text-slate-500 text-right">Bobot<br/>{idr(s.bobot_per_poin)}/poin</p>
              </div>
            </motion.div>
            );
          })}
        </div>
      )}

      {setupOpen && <TargetSetupModal onClose={() => setSetupOpen(false)} onSaved={load} />}
      {detailSel && <SalesDetailModal sel={detailSel} onClose={() => setDetailSel(null)} />}
    </div>
  );
}

/* -------------- Sub components -------------- */
function Kpi({ icon: Icon, label, value, tint = "emerald", sub }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 md:p-6 shadow-sm">
      <div className={`w-9 h-9 md:w-10 md:h-10 rounded-lg grid place-items-center bg-${tint}-50 text-${tint}-600 mb-3 md:mb-4`}>
        <Icon className="w-4 h-4 md:w-5 md:h-5" />
      </div>
      <p className="text-[10px] md:text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">{label}</p>
      <p className="font-display text-xl md:text-2xl font-bold text-slate-900 truncate">{value}</p>
      {sub && <p className="text-xs text-emerald-700 mt-1">{sub}</p>}
    </div>
  );
}
function BreakdownBox({ label, n, pt }) {
  return (
    <div className="bg-slate-50 rounded p-2">
      <p className="text-slate-500 text-[10px] md:text-xs">{label}</p>
      <p className="font-semibold text-slate-900 text-xs md:text-sm">{n} <span className="text-slate-500 text-[10px]">= {pt} pt</span></p>
    </div>
  );
}
function fmtNum(n) { return new Intl.NumberFormat("id-ID").format(n || 0); }

/* -------------- Detail Karyawan Modal (klik nama di kartu) --------------
 * Seluruhnya memakai data baris `s` yang SUDAH dimuat oleh /sales/targets —
 * tidak ada panggilan endpoint baru saat modal dibuka. */
function SalesDetailModal({ sel, onClose }) {
  const { s, divisi, insentifCalc, tiers } = sel;
  const ordered = [...(tiers || [])].sort((a, b) => (Number(a.min_pct) || 0) - (Number(b.min_pct) || 0));
  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto"
      onClick={onClose} data-testid="st-detail-modal">
      <div className="bg-white rounded-2xl w-full max-w-lg my-8" onClick={e => e.stopPropagation()}>
        <div className="border-b border-slate-200 px-6 py-4 flex items-start justify-between">
          <div className="min-w-0">
            <h3 className="font-display font-semibold text-slate-900 truncate">{s.nama}</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              {s.branch} · {s.grade_jabatan}{divisi ? ` · ${divisi}` : ""}
            </p>
          </div>
          <button onClick={onClose} data-testid="st-detail-close" className="p-2 hover:bg-slate-100 rounded-lg shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-6 space-y-5">
          {/* Ringkasan target vs realisasi */}
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="bg-slate-50 rounded-lg p-3">
              <p className="text-[10px] text-slate-500 uppercase tracking-wide">Target</p>
              <p className="font-display font-bold text-slate-900 mt-0.5">{s.target_poin} pt</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-3">
              <p className="text-[10px] text-slate-500 uppercase tracking-wide">Realisasi</p>
              <p className="font-display font-bold text-slate-900 mt-0.5">{s.realisasi_poin} pt</p>
            </div>
            <div className="bg-emerald-50 rounded-lg p-3">
              <p className="text-[10px] text-emerald-600 uppercase tracking-wide">Pencapaian</p>
              <p className="font-display font-bold text-emerald-700 mt-0.5">{s.realisasi_pct}%</p>
            </div>
          </div>

          {/* Breakdown per sumber poin */}
          <div>
            <p className="text-xs font-semibold text-slate-700 mb-2">Rincian per Sumber Poin</p>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <BreakdownBox label="HP sold" n={s.breakdown.sale_hp.n} pt={s.breakdown.sale_hp.poin} />
              <BreakdownBox label="ACC sold" n={s.breakdown.sale_acc.n} pt={s.breakdown.sale_acc.poin} />
              <BreakdownBox label="Akuisisi HP" n={s.breakdown.acq_hp.n} pt={s.breakdown.acq_hp.poin} />
              <BreakdownBox label="Akuisisi Dev" n={s.breakdown.acq_device.n} pt={s.breakdown.acq_device.poin} />
            </div>
          </div>

          {/* Upselling, hanya kalau karyawan ini punya target upselling */}
          {s.target_upselling_pct > 0 && (
            <div className="rounded-lg border border-sky-200 bg-sky-50/60 px-3 py-2.5">
              <p className="text-xs font-semibold text-sky-800 mb-1">Upselling</p>
              <p className="text-xs text-sky-700">
                Realisasi <span className="font-bold">{s.realisasi_upselling_pct}%</span> dari target {s.target_upselling_pct}%
                {" "}({s.upselling_multi_item} dari {s.upselling_total_transaksi} transaksi multi-item)
              </p>
            </div>
          )}

          {/* Insentif berjenjang — tier aktif di-highlight */}
          <div>
            <p className="text-xs font-semibold text-slate-700 mb-2">Insentif Berjenjang</p>
            <div className="space-y-1.5">
              {ordered.map((t, idx) => {
                const mn = Number(t.min_pct) || 0;
                const mx = t.max_pct === null || t.max_pct === undefined || t.max_pct === "" ? null : Number(t.max_pct);
                const active = insentifCalc.pctRealisasi >= mn && (mx === null || insentifCalc.pctRealisasi < mx);
                return (
                  <div key={idx} data-testid={`st-detail-tier-${idx}`}
                    className={`flex items-center justify-between px-3 py-2 rounded-lg border text-xs ${
                      active ? "border-emerald-300 bg-emerald-50 text-emerald-800 font-semibold" : "border-slate-200 text-slate-600"
                    }`}>
                    <span>{mn}% – {mx === null ? "∞" : `${mx}%`}</span>
                    <span>cair {t.cair_pct}%</span>
                  </div>
                );
              })}
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs mt-3">
              <div className="bg-white border border-slate-200 rounded-lg px-3 py-2">
                <p className="text-slate-500">Insentif Dasar</p>
                <p className="font-mono font-bold text-slate-900">{idr(insentifCalc.insentifDasar)}</p>
              </div>
              <div className="bg-white border border-emerald-200 rounded-lg px-3 py-2">
                <p className="text-emerald-600">Insentif Cair</p>
                <p className="font-mono font-bold text-emerald-700">{idr(insentifCalc.insentifCair)}</p>
              </div>
            </div>
            <p className="text-[10px] text-slate-400 mt-2">
              Bobot {idr(s.bobot_per_poin)}/poin · {s.insentif_rincian ? "Angka resmi dari mesin payroll." : "Pratinjau tampilan — angka resmi mengikuti slip gaji."}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

/* -------------- Target Setup Modal -------------- */
function TargetSetupModal({ onClose, onSaved }) {
  const [emps, setEmps] = useState([]);
  const [filter, setFilter] = useState("");
  const [divisiFilter, setDivisiFilter] = useState(""); // brief #27
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/employees").then(r => {
      const list = r.data.filter(e => e.is_active !== false).sort((a, b) => a.nama.localeCompare(b.nama));
      setEmps(list);
      const r0 = {};
      for (const e of list) r0[e.id] = {
        poin_target: e.poin_target || 0,
        bobot_per_poin: e.bobot_per_poin || 0,
        target_upselling_pct: e.target_upselling_pct || 0,
      };
      setRows(r0);
    });
  }, []);

  const grades = useMemo(() => Array.from(new Set(emps.map(e => e.grade_jabatan).filter(Boolean))), [emps]);
  const divisiOptions = useMemo(() => Array.from(new Set(emps.map(e => e.divisi).filter(Boolean))).sort(), [emps]);
  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return emps.filter(e =>
      (!filter || e.grade_jabatan === filter) &&
      (!divisiFilter || e.divisi === divisiFilter) &&
      (!q || (e.nama || "").toLowerCase().includes(q) || (e.nik || "").toLowerCase().includes(q))
    );
  }, [emps, filter, divisiFilter, search]);

  const upd = (id, k, v) => setRows(s => ({ ...s, [id]: { ...s[id], [k]: parseFloat(v) || 0 } }));

  const applyToGrade = () => {
    if (!filter) { toast.info("Pilih jabatan dulu untuk apply bulk"); return; }
    const first = shown[0];
    if (!first) return;
    const t = rows[first.id]?.poin_target || 0;
    const b = rows[first.id]?.bobot_per_poin || 0;
    const u = rows[first.id]?.target_upselling_pct || 0;
    setRows(s => {
      const next = { ...s };
      for (const e of shown) next[e.id] = { poin_target: t, bobot_per_poin: b, target_upselling_pct: u };
      return next;
    });
    toast.success(`Diterapkan ke ${shown.length} karyawan yang tampil`);
  };

  const save = async () => {
    setSaving(true);
    let ok = 0, fail = 0;
    for (const e of emps) {
      const cur = rows[e.id];
      if (!cur) continue;
      const changed = (cur.poin_target !== (e.poin_target || 0))
        || (cur.bobot_per_poin !== (e.bobot_per_poin || 0))
        || (cur.target_upselling_pct !== (e.target_upselling_pct || 0));
      if (!changed) continue;
      try {
        await api.patch(`/employees/${e.id}`, cur);
        ok += 1;
      } catch { fail += 1; }
    }
    setSaving(false);
    toast.success(`${ok} karyawan disimpan${fail ? ` · ${fail} gagal` : ""}`);
    onSaved();
    onClose();
  };

  // Tabel bisa berisi puluhan karyawan sekaligus dan menyentuh nominal Rp
  // (bobot per poin) — komponen ini di-mount ulang tiap dibuka (parent pakai
  // `{setupOpen && <TargetSetupModal ... />}`), jadi `active` bawaan cukup.
  const guard = useModalGuard({ onClose, onSubmit: save, busy: saving, dangerous: true });

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto"
      onClick={guard.requestClose} data-testid="setup-target-modal">
      <div ref={guard.contentRef} className="bg-white rounded-2xl w-full max-w-3xl my-8" onClick={e => e.stopPropagation()}>
        <div className="border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h3 className="font-display font-semibold text-slate-900">Setup Target Poin Karyawan</h3>
            <p className="text-xs text-slate-500">
              Isi target poin/bulan, bobot Rp per poin, &amp; target upselling (%). Hanya karyawan aktif.
            </p>
          </div>
          <button onClick={guard.requestClose} className="p-2 hover:bg-slate-100 rounded-lg"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-6">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Cari nama / NIK karyawan…"
                data-testid="setup-search-employee"
                className="h-9 pl-8 pr-8 rounded-lg border border-slate-200 text-sm w-64 focus:ring-2 focus:ring-emerald-600 focus:border-emerald-600 outline-none" />
              {search && (
                <button onClick={() => setSearch("")} data-testid="setup-search-clear"
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-slate-100 text-slate-400">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            <div className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-slate-200 bg-white">
              <Filter className="w-3.5 h-3.5 text-slate-500" />
              <select value={filter} onChange={e => setFilter(e.target.value)}
                data-testid="setup-grade-filter"
                className="text-sm bg-transparent outline-none">
                <option value="">Semua Jabatan</option>
                {grades.map(g => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
            {divisiOptions.length > 0 && (
              <div className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-slate-200 bg-white">
                <Filter className="w-3.5 h-3.5 text-slate-500" />
                <select value={divisiFilter} onChange={e => setDivisiFilter(e.target.value)}
                  data-testid="setup-filter-divisi"
                  className="text-sm bg-transparent outline-none">
                  <option value="">Semua Divisi</option>
                  {divisiOptions.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
            )}
            {filter && (
              <button onClick={applyToGrade} data-testid="btn-apply-to-grade"
                className="h-9 px-3 rounded-lg border border-emerald-200 text-emerald-800 bg-emerald-50 hover:bg-emerald-100 text-xs font-medium">
                Isi nilai baris pertama ke semua ({shown.length})
              </button>
            )}
            <p className="ml-auto text-xs text-slate-500">Menampilkan {shown.length} dari {emps.length} karyawan</p>
          </div>

          <div className="border border-slate-200 rounded-lg overflow-y-auto max-h-[50vh]">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-slate-50 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                <tr>
                  <th className="text-left py-2 px-3">Nama</th>
                  <th className="text-left py-2 px-3">Jabatan</th>
                  <th className="text-right py-2 px-3 w-32">Target Poin</th>
                  <th className="text-right py-2 px-3 w-40">Bobot / Poin (Rp)</th>
                  <th className="text-right py-2 px-3 w-36" title="Target rasio transaksi multi-item. Realisasi ditarik otomatis dari Laporan Upselling.">
                    Target Upselling (%)
                  </th>
                </tr>
              </thead>
              <tbody>
                {shown.length === 0 && (
                  <tr><td colSpan={5} className="text-center py-8 text-sm text-slate-400"
                    data-testid="setup-empty">Tidak ada karyawan yang cocok dengan pencarian.</td></tr>
                )}
                {shown.map(e => (
                  <tr key={e.id} className="border-t border-slate-100" data-testid={`setup-row-${e.id}`}>
                    <td className="py-2 px-3">
                      <p className="font-medium text-slate-900">{e.nama}</p>
                      <p className="text-[10px] text-slate-500">{e.branch}</p>
                    </td>
                    <td className="py-2 px-3 text-slate-600 text-xs">{e.grade_jabatan}</td>
                    <td className="py-2 px-3 text-right">
                      <input type="number" value={rows[e.id]?.poin_target ?? 0}
                        onChange={ev => upd(e.id, "poin_target", ev.target.value)}
                        data-testid={`setup-target-${e.id}`}
                        className="w-24 h-8 px-2 rounded border border-slate-200 text-sm text-right font-mono" />
                    </td>
                    <td className="py-2 px-3 text-right">
                      <InputRupiah value={rows[e.id]?.bobot_per_poin ?? 0}
                        onChange={n => upd(e.id, "bobot_per_poin", n)}
                        testid={`setup-bobot-${e.id}`}
                        className="w-32 h-8 px-2 rounded border border-slate-200 text-sm text-right font-mono" />
                    </td>
                    <td className="py-2 px-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        <input type="number" min={0} max={100} step={1}
                          value={rows[e.id]?.target_upselling_pct ?? 0}
                          onChange={ev => upd(e.id, "target_upselling_pct", ev.target.value)}
                          data-testid={`setup-upselling-${e.id}`}
                          className="w-20 h-8 px-2 rounded border border-slate-200 text-sm text-right font-mono" />
                        <span className="text-xs text-slate-400">%</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t border-slate-100 mt-4">
            <button onClick={guard.requestClose} className="h-10 px-4 rounded-lg border border-slate-200 text-sm">Batal</button>
            <button onClick={save} disabled={saving} data-testid="btn-save-setup"
              className="h-10 px-5 rounded-lg bg-[#059669] hover:bg-[#047857] text-white text-sm font-medium disabled:opacity-60">
              {saving ? "Menyimpan..." : "Simpan Semua"}
            </button>
          </div>
          {guard.confirming && (
            <UnsavedGuardBar onStay={guard.cancelClose} onDiscard={guard.confirmDiscard} testid="setup-target-guard-bar" />
          )}
        </div>
      </div>
    </div>
  );
}
